import { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Send, MessageSquareHeart } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ReviewSubmitDialog({ open, onOpenChange, onSubmitted }) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [persona, setPersona] = useState("shift_worker");
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (user) {
      setName(user.name || user.email?.split("@")[0] || "");
      if (user.persona) setPersona(user.persona);
    }
  }, [open, user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/reviews", { name, persona, rating: Number(rating), comment });
      toast.success("Thanks for the review!", { description: "It's now live on the landing page." });
      onOpenChange(false);
      setComment("");
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't post review");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F141F] border-white/10 text-slate-100 sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold flex items-center gap-2">
            <MessageSquareHeart className="w-5 h-5 text-indigo-300" /> Leave a review
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Tell other shift workers what CircaShift changed for you.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Your name</Label>
            <Input
              data-testid="review-input-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="First name or initials"
              className="mt-1 bg-slate-900/60 border-white/10 text-slate-100"
              required
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Persona</Label>
            <Select value={persona} onValueChange={setPersona}>
              <SelectTrigger data-testid="review-input-persona" className="mt-1 bg-slate-900/60 border-white/10 text-slate-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0F141F] border-white/10 text-slate-100">
                {["doctor","nurse","pilot","steward","train_driver","security","courier","traveler","shift_worker"].map((p) => (
                  <SelectItem key={p} value={p}>{p.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">Rating</Label>
            <div className="mt-2 flex gap-1" onMouseLeave={() => setHover(0)}>
              {[1,2,3,4,5].map((n) => (
                <button
                  type="button"
                  key={n}
                  onMouseEnter={() => setHover(n)}
                  onClick={() => setRating(n)}
                  data-testid={`review-star-${n}`}
                  className="p-1"
                  aria-label={`${n} stars`}
                >
                  <Star className={`w-7 h-7 transition ${
                    (hover || rating) >= n ? "text-amber-300 fill-amber-300" : "text-slate-600"
                  }`} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-ui text-slate-400">What changed for you?</Label>
            <Textarea
              data-testid="review-input-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="e.g. First month I've hit a real anchor sleep before every night shift…"
              rows={4}
              maxLength={600}
              required
              className="mt-1 bg-slate-900/60 border-white/10 text-slate-100 resize-none"
            />
            <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500 mt-1 text-right">
              {comment.length}/600
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy || !name.trim() || comment.trim().length < 4}
            data-testid="review-submit-btn"
            className="w-full rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
          >
            <Send className="w-4 h-4 mr-1" /> {busy ? "Posting…" : "Post review"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
